import { SortOrder } from '@blk/aw-bff-typescript-models';
import { Sector } from '@blk/awt-aladdinportfoliobuilderserver';
import { IRowNode } from 'ag-grid-community';
import { flatMap, flatMapDepth, orderBy } from 'lodash';

export interface ValueAsObject {
  market: number;
  toString: () => string;
  weight: number;
}

function traverseToLeafs(children: Sector[]): Sector[] {
  return flatMap(children, (child) => {
    if (child.children.length === 0) {
      return [child];
    }
    return traverseToLeafs(child.children);
  });
}

export function traverseContributorsDepth(topLevelContributors: Sector[], depth: number): Sector[] {
  if (depth <= 1) {
    return topLevelContributors;
  }
  return flatMapDepth(topLevelContributors, (sector) => traverseContributorsDepth(sector.children, depth - 1), depth);
}

export function traverseMaxDepth(topLevelContributors: Sector, depth: number): Sector[] | Sector {
  if (depth === 0 || topLevelContributors?.children?.length === 0) {
    return topLevelContributors;
  }
  return flatMapDepth(topLevelContributors.children, (sector) => traverseMaxDepth(sector, depth - 1), depth);
}

export function traverseGroupDepth(topLevelContributors: Sector[], parent: Sector, depth: number): Sector[] | Sector {
  if (topLevelContributors.length === 0) {
    return parent;
  }
  if (depth === 0) {
    return topLevelContributors;
  }
  return flatMapDepth(topLevelContributors, (sector) => traverseGroupDepth(sector.children, sector, depth - 1), depth);
}

export function traverseContributors(topLevelContributors: Sector[], leafsOnly: boolean): Sector[] {
  if (leafsOnly) {
    return traverseToLeafs(topLevelContributors);
  }
  return topLevelContributors;
}

const isValueAsObject = (value: ValueAsObject | number[]): value is ValueAsObject =>
  value.hasOwnProperty('market') && value.hasOwnProperty('weight');

export const numComparator = (
  valueA: ValueAsObject | number[],
  valueB: ValueAsObject | number[],
  nodeA: IRowNode,
  nodeB: IRowNode
) => {
  if (isValueAsObject(valueA) && isValueAsObject(valueB) && nodeA.aggData && nodeB.aggData) {
    if (valueA.market === valueB.market) {
      return 0;
    }
    if (valueA.market > valueB.market) {
      return 1;
    }
    return -1;
  } else if (!isValueAsObject(valueA) && !isValueAsObject(valueB)) {
    if (valueA[0] === valueB[0]) {
      return 0;
    }
    if (valueA[0] > valueB[0]) {
      return 1;
    }
    return -1;
  }
  return 0;
};

export const getSortedSectors = (sectors: Sector[], sortOrder: SortOrder, numberOfChartedSectors?: number) => {
  const tmpSectors = [...sectors];
  tmpSectors.pop();
  const sortedSectors = orderBy(tmpSectors, ['contribution'], sortOrder);
  sortedSectors.push(sectors[sectors.length - 1]);

  // if number of charted sectors is given only shown top number of sectors and recalculate the total value
  if (numberOfChartedSectors && sortOrder === 'desc') {
    numberOfChartedSectors = numberOfChartedSectors > sectors.length - 1 ? sectors.length - 1 : numberOfChartedSectors;
    const topSortedSectors = sortedSectors.slice(0, numberOfChartedSectors);
    const totalContribution = sortedSectors
      .slice(numberOfChartedSectors)
      .reduce((sum, sector) => sum + sector.contribution, 0);
    const totalPositionContributors = sortedSectors
      .slice(numberOfChartedSectors)
      .flatMap((items) => items.positionContributors);
    const resultSortedSectors = [
      ...topSortedSectors,
      {
        ...sectors[sectors.length - 1],
        positionContributors: totalPositionContributors,
        contribution: totalContribution,
      },
    ];
    return resultSortedSectors;
  }
  return sortedSectors;
};
